export const FEATURE_FLAG_UI_ACTION = {
    NONE: 'NONE',
    SHOW_UPGRADE_MODAL: 'SHOW_UPGRADE_MODAL',
    SHOW_TEAM_UPGRADE_MODAL: 'SHOW_TEAM_UPGRADE_MODAL',
    HIDE: 'HIDE',
    DISABLE_RETRY: 'DISABLE_RETRY'
};

export const FEATURE_FLAG_ACCESS_REASON = {
    ALLOWED: 'ALLOWED',
    LOADING: 'LOADING',
    FLAG_UNAVAILABLE: 'FLAG_UNAVAILABLE',
    FLAG_DISABLED: 'FLAG_DISABLED',
    PLAN_REQUIRED: 'PLAN_REQUIRED',
    TEAM_PLAN_REQUIRED: 'TEAM_PLAN_REQUIRED',
    ADMIN_REQUIRED: 'ADMIN_REQUIRED',
    CUSTOM_ACCESS_REQUIRED: 'CUSTOM_ACCESS_REQUIRED',
    TEAM_CONTEXT_REQUIRED: 'TEAM_CONTEXT_REQUIRED',
    INVALID_CONFIGURATION: 'INVALID_CONFIGURATION'
};

const PLAN_TYPE_ORDER = ['PERSONAL', 'STANDARD', 'PRO', 'BETA', 'PLUS', 'REVIEWER', 'BELIEVER', 'UNLIMITED'];
const PLAN_TYPE_SET = new Set(PLAN_TYPE_ORDER);
const TEAM_PRO_ALLOWED_PLAN_SET = new Set(['PRO', 'BETA', 'BELIEVER', 'UNLIMITED']);
const WORKSPACE_TYPE_SET = new Set(['PERSONAL', 'TEAM']);
const MEMBERSHIP_STATUS_SET = new Set(['ACTIVE', 'INACTIVE', 'PENDING', 'REMOVED']);
const PLAN_LABELS = {
    PERSONAL: 'Personal',
    STANDARD: 'Standard',
    PRO: 'Pro',
    BETA: 'Beta',
    PLUS: 'Plus',
    REVIEWER: 'Reviewer',
    BELIEVER: 'Believer',
    UNLIMITED: 'Unlimited'
};

const LEGACY_TARGET_PLAN_MAP = {
    STANDARD: ['STANDARD'],
    PRO: ['PRO', 'BETA'],
    BETA: ['BETA']
};

const normalizeUserId = value => String(value || '').trim().toLowerCase();

const normalizePlanType = value => {
    const normalized = String(value || '').trim().toUpperCase();
    return PLAN_TYPE_SET.has(normalized) ? normalized : undefined;
};

const normalizeTargetUsers = value => {
    const normalized = String(value || '').trim().toUpperCase();
    const supported = [
        'ALL',
        'STANDARD',
        'PRO',
        'PLAN_LIST',
        'TEAMS',
        'TEAMS_PRO',
        'BETA',
        'ADMIN',
        'CUSTOM'
    ];
    return supported.includes(normalized) ? normalized : undefined;
};

export const normalizeAllowedPlanTypes = planTypes => {
    if (!Array.isArray(planTypes)) {
        return [];
    }
    const deduped = new Set();
    planTypes.forEach(plan => {
        const normalized = normalizePlanType(plan);
        if (normalized) {
            deduped.add(normalized);
        }
    });
    return [...deduped].sort((a, b) => PLAN_TYPE_ORDER.indexOf(a) - PLAN_TYPE_ORDER.indexOf(b));
};

export const getAllowedPlanTypesForFlag = flag => {
    const targetUsers = normalizeTargetUsers(flag?.targetUsers);
    if (targetUsers === 'PLAN_LIST') {
        return normalizeAllowedPlanTypes(flag?.allowedPlanTypes);
    }
    return LEGACY_TARGET_PLAN_MAP[targetUsers] || [];
};

const buildDeniedDecision = (reason, uiAction, details = {}) => ({
    allowed: false,
    reason,
    uiAction,
    requiresUpgrade: uiAction === FEATURE_FLAG_UI_ACTION.SHOW_UPGRADE_MODAL,
    requiresTeamUpgrade: uiAction === FEATURE_FLAG_UI_ACTION.SHOW_TEAM_UPGRADE_MODAL,
    details
});

const buildAllowedDecision = (details = {}) => ({
    allowed: true,
    reason: FEATURE_FLAG_ACCESS_REASON.ALLOWED,
    uiAction: FEATURE_FLAG_UI_ACTION.NONE,
    requiresUpgrade: false,
    requiresTeamUpgrade: false,
    details
});

const normalizeWorkspaceType = value => {
    const normalized = String(value || '').trim().toUpperCase();
    return WORKSPACE_TYPE_SET.has(normalized) ? normalized : undefined;
};

const normalizeMembershipStatus = value => {
    const normalized = String(value || '').trim().toUpperCase();
    return MEMBERSHIP_STATUS_SET.has(normalized) ? normalized : undefined;
};

const normalizeFeatureAccessContext = context => {
    if (!context) {
        return undefined;
    }

    const workspaceType = normalizeWorkspaceType(context.workspaceType);
    const workspacePlanType = normalizePlanType(context.workspacePlanType);
    const membershipStatus = normalizeMembershipStatus(context.membershipStatus);
    const workspaceId = String(context.workspaceId || '').trim() || undefined;
    const membershipRole = String(context.membershipRole || '').trim() || undefined;

    if (!workspaceType && !workspacePlanType && !membershipStatus && !workspaceId && !membershipRole) {
        return undefined;
    }

    return {
        workspaceType,
        workspacePlanType,
        membershipStatus,
        workspaceId,
        membershipRole
    };
};

const isActiveTeamMember = context => normalizeMembershipStatus(context?.membershipStatus) === 'ACTIVE';

export const evaluateFeatureFlagForUser = (flag, user, options = {}) => {
    const { loading = false, featureAccessContext } = options;

    if (loading) {
        return {
            allowed: undefined,
            reason: FEATURE_FLAG_ACCESS_REASON.LOADING,
            uiAction: FEATURE_FLAG_UI_ACTION.NONE,
            requiresUpgrade: false,
            requiresTeamUpgrade: false,
            details: {}
        };
    }

    if (!flag) {
        return buildDeniedDecision(
            FEATURE_FLAG_ACCESS_REASON.FLAG_UNAVAILABLE,
            FEATURE_FLAG_UI_ACTION.DISABLE_RETRY
        );
    }

    if (!flag.enabled) {
        return buildDeniedDecision(
            FEATURE_FLAG_ACCESS_REASON.FLAG_DISABLED,
            FEATURE_FLAG_UI_ACTION.DISABLE_RETRY
        );
    }

    const targetUsers = normalizeTargetUsers(flag.targetUsers);
    if (!targetUsers) {
        return buildDeniedDecision(
            FEATURE_FLAG_ACCESS_REASON.INVALID_CONFIGURATION,
            FEATURE_FLAG_UI_ACTION.DISABLE_RETRY,
            { targetUsers: flag.targetUsers }
        );
    }
    const userPlanType = normalizePlanType(user?.planType);
    const userRole = String(user?.role || '').trim().toUpperCase();
    const userId = normalizeUserId(user?.id || user?._id);
    const normalizedFeatureAccessContext = normalizeFeatureAccessContext(featureAccessContext);

    if (targetUsers === 'ALL') {
        return buildAllowedDecision({ targetUsers });
    }

    if (targetUsers === 'ADMIN') {
        if (userRole === 'ADMIN') {
            return buildAllowedDecision({ targetUsers });
        }
        return buildDeniedDecision(FEATURE_FLAG_ACCESS_REASON.ADMIN_REQUIRED, FEATURE_FLAG_UI_ACTION.HIDE, {
            targetUsers
        });
    }

    if (targetUsers === 'CUSTOM') {
        const customUserIds = Array.isArray(flag.customUserIds)
            ? flag.customUserIds.map(normalizeUserId)
            : [];
        if (userId && customUserIds.includes(userId)) {
            return buildAllowedDecision({ targetUsers });
        }
        return buildDeniedDecision(
            FEATURE_FLAG_ACCESS_REASON.CUSTOM_ACCESS_REQUIRED,
            FEATURE_FLAG_UI_ACTION.HIDE,
            { targetUsers }
        );
    }

    if (targetUsers === 'TEAMS' || targetUsers === 'TEAMS_PRO') {
        const isTeamWorkspace = normalizeWorkspaceType(normalizedFeatureAccessContext?.workspaceType) === 'TEAM';
        if (!isTeamWorkspace || !isActiveTeamMember(normalizedFeatureAccessContext)) {
            return buildDeniedDecision(
                FEATURE_FLAG_ACCESS_REASON.TEAM_CONTEXT_REQUIRED,
                FEATURE_FLAG_UI_ACTION.DISABLE_RETRY,
                { targetUsers }
            );
        }

        if (targetUsers === 'TEAMS_PRO') {
            const workspacePlanType = normalizePlanType(normalizedFeatureAccessContext?.workspacePlanType);
            if (!workspacePlanType || !TEAM_PRO_ALLOWED_PLAN_SET.has(workspacePlanType)) {
                return buildDeniedDecision(
                    FEATURE_FLAG_ACCESS_REASON.TEAM_PLAN_REQUIRED,
                    FEATURE_FLAG_UI_ACTION.SHOW_TEAM_UPGRADE_MODAL,
                    {
                        targetUsers,
                        workspacePlanType
                    }
                );
            }
        }

        return buildAllowedDecision({ targetUsers });
    }

    const allowedPlanTypes = getAllowedPlanTypesForFlag(flag);
    if (allowedPlanTypes.length === 0) {
        return buildDeniedDecision(
            FEATURE_FLAG_ACCESS_REASON.INVALID_CONFIGURATION,
            FEATURE_FLAG_UI_ACTION.DISABLE_RETRY,
            { targetUsers }
        );
    }

    const hasPlanAccess = userPlanType && (
        allowedPlanTypes.includes(userPlanType) ||
        (userPlanType === 'REVIEWER' && allowedPlanTypes.includes('PLUS'))
    );
    if (!hasPlanAccess) {
        return buildDeniedDecision(
            FEATURE_FLAG_ACCESS_REASON.PLAN_REQUIRED,
            FEATURE_FLAG_UI_ACTION.SHOW_UPGRADE_MODAL,
            {
                targetUsers,
                allowedPlanTypes
            }
        );
    }

    return buildAllowedDecision({
        targetUsers,
        allowedPlanTypes
    });
};

export const getFeatureFlagTargetLabel = flag => {
    const targetUsers = normalizeTargetUsers(flag?.targetUsers);
    if (targetUsers === 'PLAN_LIST') {
        const allowedPlans = getAllowedPlanTypesForFlag(flag);
        if (allowedPlans.length === 0) {
            return 'PLAN_LIST';
        }
        return `PLAN_LIST (${allowedPlans.join(' + ')})`;
    }
    return targetUsers || String(flag?.targetUsers || 'UNKNOWN');
};

const toReadablePlanList = planTypes => {
    const plans = normalizeAllowedPlanTypes(planTypes).map(plan => PLAN_LABELS[plan] || plan);
    if (plans.length === 0) return '';
    if (plans.length === 1) return plans[0];
    if (plans.length === 2) return `${plans[0]} or ${plans[1]}`;
    return `${plans.slice(0, -1).join(', ')}, or ${plans[plans.length - 1]}`;
};

export const getPlanUpgradeMessage = (planTypes, featureName = 'this feature') => {
    const readablePlans = toReadablePlanList(planTypes);
    if (!readablePlans) {
        return `Upgrade your plan to use ${featureName}.`;
    }
    return `Upgrade to ${readablePlans} to use ${featureName}.`;
};

export const getFeatureFlagUpgradeMessage = (flag, featureName = 'this feature') =>
    getPlanUpgradeMessage(getAllowedPlanTypesForFlag(flag), featureName);

export const mapTranscriptionErrorToUiAction = ({ error, defaultDecision } = {}) => {
    const code = String(error?.code || '').trim().toUpperCase();

    if (code === 'FEATURE_PLAN_UPGRADE_REQUIRED') {
        return {
            openUpgradeModal: true,
            openTeamUpgradeModal: false,
            showMessage: null,
            disableRetry: false
        };
    }
    if (code === 'FEATURE_TEAM_PLAN_UPGRADE_REQUIRED') {
        return {
            openUpgradeModal: false,
            openTeamUpgradeModal: true,
            showMessage: null,
            disableRetry: false
        };
    }
    if (code === 'FEATURE_TEAM_CONTEXT_REQUIRED') {
        return {
            openUpgradeModal: false,
            openTeamUpgradeModal: false,
            showMessage: 'Audio transcription requires an active team workspace context.',
            disableRetry: true
        };
    }
    if (code === 'FEATURE_TEMPORARILY_UNAVAILABLE') {
        return {
            openUpgradeModal: false,
            openTeamUpgradeModal: false,
            showMessage: 'Audio transcription is temporarily unavailable. Please retry in a moment.',
            disableRetry: false
        };
    }
    if (code === 'FEATURE_DISABLED') {
        return {
            openUpgradeModal: false,
            openTeamUpgradeModal: false,
            showMessage: 'Audio transcription is currently disabled',
            disableRetry: true
        };
    }
    if (code === 'INSUFFICIENT_CREDITS') {
        return {
            openUpgradeModal: false,
            openTeamUpgradeModal: false,
            showMessage: null,
            disableRetry: false
        };
    }

    if (defaultDecision?.requiresUpgrade) {
        return {
            openUpgradeModal: true,
            openTeamUpgradeModal: false,
            showMessage: null,
            disableRetry: false
        };
    }
    if (defaultDecision?.requiresTeamUpgrade) {
        return {
            openUpgradeModal: false,
            openTeamUpgradeModal: true,
            showMessage: null,
            disableRetry: false
        };
    }

    return {
        openUpgradeModal: false,
        openTeamUpgradeModal: false,
        showMessage: error?.message || null,
        disableRetry: false
    };
};
